import React from 'react';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import RoomBookingUserAvatar from '../RoomBookingUserAvatar';
import ChatWithUserIconButton from '../chat/ChatWithUserIconButton';
import UserBandAffiliationLine from '../UserBandAffiliationLine';
import LayeredTutorBookingAvatars from './LayeredTutorBookingAvatars';
import TutorBookingScheduleBlock from './TutorBookingScheduleBlock';
import TutorLessonSessionTypeChip from './TutorLessonSessionTypeChip';
import {
  resolveTutorEventLocationLabel,
  TUTOR_CALENDAR_COLORS,
  TutorCalendarLessonEvent,
} from '../../utils/tutorCalendarEvents';
import { formatTutorBookingScheduleLabels } from '../../utils/tutorBookingChangeProposal';
import {
  getTutorBookingStatusChipColor,
  getTutorBookingStatusChipVariant,
  tutorBookingStatusLabel,
} from '../../utils/tutorBookingStatus';
import { tutorBookingDetailsPath } from '../../utils/tutorBookingDetailsPath';
import { useThemeContext } from '../../contexts/ThemeContext';

export interface TutorCalendarEventDetailsDialogProps {
  open: boolean;
  event: TutorCalendarLessonEvent | null;
  audience: 'host' | 'tutor';
  onClose: () => void;
  canDeleteBlock?: boolean;
  deletingBlock?: boolean;
  onRequestDeleteBlock?: () => void;
  /** Host unified calendar: resolve room names from the space room list. */
  rooms?: { id: string; name: string }[];
  /**
   * Calendar grid IANA timezone (space / tutor schedule). Used for manual/external
   * blocks and as a fallback when a booking event has no `timezone`.
   * Booking-backed events prefer `event.timezone` from the API.
   */
  timezone?: string | null;
}

function formatDurationMinutes(minutes: number, minLabel: string, hrLabel: string, hrsLabel: string): string {
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (hours === 0) return `${remaining} ${minLabel}`;
  if (remaining === 0) return `${hours} ${hours > 1 ? hrsLabel : hrLabel}`;
  return `${hours} ${hours > 1 ? hrsLabel : hrLabel} ${remaining} ${minLabel}`;
}

export default function TutorCalendarEventDetailsDialog({
  open,
  event,
  audience,
  onClose,
  canDeleteBlock = false,
  deletingBlock = false,
  onRequestDeleteBlock,
  rooms = [],
  timezone = null,
}: TutorCalendarEventDetailsDialogProps) {
  const { t, i18n } = useTranslation();
  const { mode: themeMode } = useThemeContext();

  const isBlock = event?.kind === 'manual' || event?.kind === 'external';
  const isManualBlock = event?.kind === 'manual';
  const isBooking = Boolean(event?.bookingId) && !isBlock;

  const studentName =
    event?.studentName?.trim() ||
    (isBooking ? t('tutorCalendar.walkInStudentLabel', 'Walk-in') : '');

  const studentPhone = event?.studentPhoneNumber?.trim() || null;
  const studentBand = event?.bandName?.trim() || null;
  const studentUserId = event?.studentUserId?.trim() || null;
  const tutorName = event?.tutorName?.trim() || '';
  const spaceName = event?.spaceName?.trim() || null;

  const lessonTitle =
    event?.lessonName?.trim() ||
    event?.title?.trim() ||
    t('tutorCalendar.lessonFallback', 'Lesson');

  const locationLabel = event
    ? resolveTutorEventLocationLabel(event, {
        rooms,
        onlineLabel: t('spaceCalendar.onlineLesson', 'Online'),
        unassignedRoomLabel: t('spaceCalendar.noRoomAssigned', 'No room'),
      })
    : null;

  // Booking events store UTC instants derived from API wall clocks + booking.timezone.
  // Prefer that response timezone so labels match list/detail booking cards.
  const scheduleTimezone = event?.timezone?.trim() || timezone?.trim() || null;
  const schedule = event
    ? formatTutorBookingScheduleLabels(
        event.startTime,
        event.endTime,
        i18n.language,
        (minutes) =>
          formatDurationMinutes(
            minutes,
            t('roomBookingRequests.min', 'min'),
            t('roomBookingRequests.hr', 'hr'),
            t('roomBookingRequests.hrs', 'hrs'),
          ),
        scheduleTimezone
          ? { source: 'proposal', timezone: scheduleTimezone }
          : undefined,
      )
    : null;

  const accentColor = isManualBlock
    ? TUTOR_CALENDAR_COLORS.manual
    : event?.kind === 'external'
      ? TUTOR_CALENDAR_COLORS.external
      : event?.kind === 'pending'
        ? TUTOR_CALENDAR_COLORS.pending
        : TUTOR_CALENDAR_COLORS.confirmed;

  const bookingDetailsTo =
    event?.bookingId && isBooking
      ? tutorBookingDetailsPath(audience, event.bookingId)
      : null;

  const statusChip = isBlock ? (
    <Chip
      label={
        isManualBlock
          ? t('tutorCalendar.statusManualBlock', 'Manual block')
          : t('tutorCalendar.statusExternal', 'Google Calendar')
      }
      sx={{
        flexShrink: 0,
        height: 36,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: '0.8125rem',
        bgcolor: alpha(accentColor, themeMode === 'dark' ? 0.28 : 0.18),
        color: themeMode === 'dark' ? '#FFF8E7' : '#5C3D00',
        border: '1px solid',
        borderColor: alpha(accentColor, 0.55),
        '& .MuiChip-label': { px: 1.25 },
      }}
    />
  ) : event?.status ? (
    <Chip
      color={getTutorBookingStatusChipColor(event.status)}
      variant={getTutorBookingStatusChipVariant(event.status)}
      label={tutorBookingStatusLabel(t, event.status)}
      sx={{
        flexShrink: 0,
        height: 36,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: '0.8125rem',
        '& .MuiChip-label': { px: 1.25 },
      }}
    />
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={() => !deletingBlock && onClose()}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          margin: { xs: 1, sm: 2 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: '520px' },
          overflow: 'hidden',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
          background: themeMode === 'dark' ? '#1F2129' : '#FFFFFF',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.25, pr: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {isBlock
              ? t('tutorCalendar.blockDetails', 'Block details')
              : t('tutorCalendar.lessonDetails', 'Lesson details')}
          </Typography>
          {statusChip}
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, px: { xs: 2, sm: 2.5 }, pb: 2 }}>
        {event && schedule ? (
          <Stack spacing={2}>
            {isBlock ? (
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(accentColor, themeMode === 'dark' ? 0.24 : 0.16),
                    color: accentColor,
                  }}
                >
                  {isManualBlock ? (
                    <EventBusyRoundedIcon sx={{ fontSize: 26 }} />
                  ) : (
                    <SyncRoundedIcon sx={{ fontSize: 26 }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {event.title?.trim() || t('spaceCalendar.block', 'Block')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.4 }}>
                    {isManualBlock
                      ? t(
                          'tutorCalendar.blockDetailsHint',
                          'This slot is blocked on the tutor calendar — it is not a lesson booking.',
                        )
                      : t(
                          'tutorCalendar.externalBlockHint',
                          'Imported from Google Calendar. It is not a Band Space booking.',
                        )}
                  </Typography>
                  {audience === 'host' && event.tutorName?.trim() ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} noWrap>
                      {t('hostTutorBookings.tutor', 'Tutor')}:{' '}
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {event.tutorName.trim()}
                      </Box>
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {audience === 'host' && tutorName ? (
                  <LayeredTutorBookingAvatars
                    primaryImageUrl={event.studentProfileImageUrl}
                    primaryName={studentName}
                    primaryTooltip={t('hostTutorBookings.user', 'Student')}
                    secondaryImageUrl={event.tutorProfileImageUrl}
                    secondaryName={tutorName}
                    secondaryTooltip={t('hostTutorBookings.tutor', 'Tutor')}
                    size={48}
                  />
                ) : (
                  <RoomBookingUserAvatar
                    userProfileImageUrl={event.studentProfileImageUrl}
                    displayName={studentName}
                    size={48}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      fontWeight={700}
                      noWrap
                      sx={{ lineHeight: 1.3, minWidth: 0 }}
                    >
                      {studentName}
                    </Typography>
                    {studentUserId ? (
                      <ChatWithUserIconButton
                        partnerUserId={studentUserId}
                        partnerName={studentName || null}
                        basePath={audience === 'host' ? '/host/chats' : '/tutor/chats'}
                        sx={{ flexShrink: 0 }}
                      />
                    ) : null}
                  </Stack>
                  {studentPhone ? (
                    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ mt: 0.35 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="a"
                        href={`tel:${studentPhone}`}
                        sx={{ textDecoration: 'none' }}
                        noWrap
                      >
                        {studentPhone}
                      </Typography>
                    </Stack>
                  ) : null}
                  {studentBand ? <UserBandAffiliationLine bandName={studentBand} /> : null}
                  {audience === 'host' && tutorName ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="span"
                      sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}
                      noWrap
                    >
                      <Trans
                        i18nKey={
                          spaceName
                            ? 'hostTutorBookings.lessonWithAt'
                            : 'hostTutorBookings.lessonWith'
                        }
                        defaults={
                          spaceName
                            ? 'Lesson with <tutorLink>{{tutor}}</tutorLink> at <spaceLink>{{space}}</spaceLink>'
                            : 'Lesson with <tutorLink>{{name}}</tutorLink>'
                        }
                        values={
                          spaceName
                            ? { tutor: tutorName, space: spaceName }
                            : { name: tutorName }
                        }
                        components={{
                          tutorLink: event.tutorId ? (
                            <MuiLink
                              component={RouterLink}
                              to={`/host/tutors/${event.tutorId}`}
                              variant="body2"
                              underline="hover"
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                verticalAlign: 'baseline',
                              }}
                            />
                          ) : (
                            <Box
                              component="span"
                              sx={{ fontWeight: 600, color: 'text.primary' }}
                            />
                          ),
                          spaceLink:
                            spaceName && event.spaceId ? (
                              <MuiLink
                                component={RouterLink}
                                to={`/host/spaces/${event.spaceId}`}
                                variant="body2"
                                underline="hover"
                                sx={{
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  verticalAlign: 'baseline',
                                }}
                              />
                            ) : (
                              <Box
                                component="span"
                                sx={{ fontWeight: 600, color: 'text.primary' }}
                              />
                            ),
                        }}
                      />
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            )}

            <TutorBookingScheduleBlock schedule={schedule} />

            {!isBlock ? (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MusicNoteRoundedIcon
                    sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                  />
                  <Stack spacing={0.75} minWidth={0} sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {lessonTitle}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <TutorLessonSessionTypeChip sessionType={event.sessionType} />
                      {locationLabel && event.sessionType !== 'ONLINE' ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<PlaceOutlinedIcon />}
                          label={locationLabel}
                          sx={{ height: 24, maxWidth: '100%' }}
                        />
                      ) : null}
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={deletingBlock}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          {t('common.close', 'Close')}
        </Button>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {canDeleteBlock && isManualBlock && event?.calendarEventId && onRequestDeleteBlock ? (
            <Button
              onClick={onRequestDeleteBlock}
              variant="outlined"
              color="error"
              disabled={deletingBlock}
              sx={{
                borderRadius: 2,
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {t('tutorCalendar.deleteBlock', 'Delete block')}
            </Button>
          ) : null}
          {bookingDetailsTo ? (
            <Button
              component={RouterLink}
              to={bookingDetailsTo}
              variant="contained"
              onClick={onClose}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: 2,
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {t('tutorCalendar.viewBookingDetails', 'View booking details')}
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
